import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, MapPin } from "lucide-react";
import { TikTokIcon } from "./icons";
import { LINKS, INFO } from "../../data";

const navItems = [
  { label: "Menu", href: "#menu" },
  { label: "Flavors", href: "#sauces" },
  { label: "Our Story", href: "#story" },
  { label: "Find Us", href: "#location" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color] duration-300 border-b ${
        scrolled ? "bg-black/90 backdrop-blur-xl border-neon-green/20" : "bg-black border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 h-[72px] flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-heading text-3xl sm:text-4xl text-neon-green neon-glow-green leading-none">67</span>
          <span className="font-heading text-xl sm:text-2xl text-neon-yellow leading-none tracking-wide">WINGS</span>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navItems.map((n) => (
            <a
              key={n.label}
              href={n.href}
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-body text-sm uppercase tracking-[0.15em] text-neutral-300 hover:text-neon-green transition-colors duration-200"
            >
              {n.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/order"
            data-testid="nav-order-btn"
            className="hidden sm:inline-flex font-heading text-lg tracking-wide bg-neon-green text-black px-5 py-2 hover:bg-neon-yellow transition-colors duration-200"
          >
            Order Now
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden text-neon-green p-1"
            aria-label="Toggle menu"
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-black border-t border-neon-green/20"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {navItems.map((n) => (
                <a
                  key={n.label}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className="font-heading text-3xl text-neutral-200 hover:text-neon-green transition-colors"
                >
                  {n.label}
                </a>
              ))}
              <Link
                to="/order"
                onClick={() => setOpen(false)}
                data-testid="mobile-order-btn"
                className="mt-2 text-center font-heading text-2xl bg-neon-green text-black py-3"
              >
                Order Online
              </Link>
              <div className="flex items-center gap-5 pt-2 text-neutral-400">
                <a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-instagram-link"><Instagram size={22} /></a>
                <a href={LINKS.maps} target="_blank" rel="noopener noreferrer" aria-label="Location on Google Maps" data-testid="mobile-maps-link"><MapPin size={22} /></a>
                <a href={LINKS.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="mobile-tiktok-link"><TikTokIcon size={22} /></a>
                <span className="font-body text-xs">{INFO.instagram}</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Nav;
